import { defaultAxiosInstance, formDataAxiosInstance } from '../config/axiosConfig';
import config, { getApiKey } from '../config/environment';

// API Service class with all endpoints
class ApiService {
  constructor() {
    this.httpClient = defaultAxiosInstance;
    this.formDataClient = formDataAxiosInstance;
  }

  // LLM Providers endpoints
  async getProviders(activeOnly = true, limit) {
    try {
      const response = await this.httpClient.get('/llm-providers/', {
        params: { active_only: activeOnly, ...(limit != null ? { limit } : {}) }
      });
      return response.data.providers || response.data;
    } catch (error) {
      throw error;
    }
  }

  async createProvider(data) {
    try {
      const response = await this.httpClient.post('/llm-providers/', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateProvider(id, data) {
    try {
      const response = await this.httpClient.put(`/llm-providers/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteProvider(id) {
    try {
      const response = await this.httpClient.delete(`/llm-providers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ── User LLM Keys (Surface A — "My LLM Keys", header x-api-key) ──────────
  async getUserLLMKeys() {
    try {
      const response = await this.httpClient.get('/user-llm-keys/');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserLLMKeysEffective() {
    try {
      const response = await this.httpClient.get('/user-llm-keys/effective');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async setUserLLMKey(providerId, payload) {
    try {
      const response = await this.httpClient.put(`/user-llm-keys/${providerId}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteUserLLMKey(providerId) {
    try {
      const response = await this.httpClient.delete(`/user-llm-keys/${providerId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // LLM Models endpoints
  async getModels(activeOnly = true) {
    try {
      const response = await this.httpClient.get('/llm-models/', {
        params: { active_only: activeOnly }
      });
      return response.data.models || response.data;
    } catch (error) {
      throw error;
    }
  }

  async getModelsByProvider(providerId, activeOnly = true) {
    try {
      const response = await this.httpClient.get(`/llm-models/provider/${providerId}`, {
        params: { active_only: activeOnly }
      });
      return response.data.models || response.data;
    } catch (error) {
      throw error;
    }
  }

  async createModel(data) {
    try {
      const response = await this.httpClient.post('/llm-models/', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateModel(id, data) {
    try {
      const response = await this.httpClient.put(`/llm-models/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteModel(id) {
    try {
      const response = await this.httpClient.delete(`/llm-models/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // AI Generation endpoint
  async generateRoleAndGoal(data) {
    try {
      const response = await this.httpClient.post('/ask/fouray', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Rules book endpoints
  async uploadKnowledgeBase(formData) {
    try {
      // Use the specialized form data client
      const response = await this.formDataClient.post('/file-dim1/upload-dim1', formData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // File Management endpoints
  async uploadFiles(formData) {
    try {
      // Use the specialized form data client for file uploads
      // The actual endpoint is /upload-dim1 based on the backend code
      const response = await this.formDataClient.post('/file-dim1/upload-dim1', formData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFiles(folderId = null) {
    try {
      const params = folderId ? { folder_id: folderId } : {};
      const response = await this.httpClient.get('/file-metadata/files', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFile(fileId) {
    try {
      const response = await this.httpClient.get(`/file-metadata/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateFileStatus(fileId, status) {
    try {
      const response = await this.httpClient.put(`/file-metadata/files/${fileId}/status`, { status });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteFile(fileId) {
    try {
      const response = await this.httpClient.delete(`/file-metadata/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Rules book Folder endpoints
  async createKnowledgeBaseFolder(folderConfig) {
    try {
      const response = await this.httpClient.post('/folders/', folderConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolders() {
    try {
      const response = await this.httpClient.get('/folders/');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolder(folderId) {
    try {
      const response = await this.httpClient.get(`/folders/${folderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderWithFiles(folderId) {
    try {
      const response = await this.httpClient.get(`/folders/${folderId}/with-files`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderConfiguration(folderId) {
    try {
      const response = await this.httpClient.get(`/folders/${folderId}/configuration`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateKnowledgeBaseFolder(folderId, folderConfig) {
    try {
      const response = await this.httpClient.put(`/folders/${folderId}`, folderConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteKnowledgeBaseFolder(folderId) {
    try {
      const response = await this.httpClient.delete(`/folders/${folderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Customer Data Folder endpoints
  async createKnowledgeBaseFolderD3(folderConfig) {
    try{
      const response = await this.httpClient.post('/folders-dim3/', folderConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFoldersD3() {
    try {
      const response = await this.httpClient.get('/folders-dim3/');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderTreeD3() {
    try {
      const response = await this.httpClient.get('/folders-dim3/tree');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderD3(folderId) {
    try {
      const response = await this.httpClient.get(`/folders-dim3/${folderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderWithFilesD3(folderId) {
    try {
      const response = await this.httpClient.get(`/folders-dim3/${folderId}/with-files`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getKnowledgeBaseFolderConfigurationD3(folderId) {
    try {
      const response = await this.httpClient.get(`/folders-dim3/${folderId}/configuration`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateKnowledgeBaseFolderD3(folderId, folderConfig) {
    try {
      const response = await this.httpClient.put(`/folders-dim3/${folderId}`, folderConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteKnowledgeBaseFolderD3(folderId) {
    try {
      const response = await this.httpClient.delete(`/folders-dim3/${folderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Customer Data File endpoints
  async uploadFileD3(formData) {
    try {
      const response = await this.formDataClient.post('/file-dim3/upload-dim3', formData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async uploadWebUrlD3(payload) {
    try {
      const response = await this.httpClient.post('/web-dim3/upload', payload, {
        // Web scraping can exceed the default 30s timeout.
        timeout: 300000
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async importGoogleDriveD3(payload) {
    try {
      // Server processes the imported files asynchronously (returns 202), but
      // token validation / Drive metadata lookup can exceed the default 30s timeout.
      const response = await this.httpClient.post('/file-dim3/import-google-drive', payload, {
        timeout: 120000
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFilesD3(folderId = null) {
    try {
      const params = folderId ? { folder_id: folderId } : {};
      const response = await this.httpClient.get('/file-metadata-dim3/files', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFileD3(fileId) {
    try {
      const response = await this.httpClient.get(`/file-metadata-dim3/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateFileStatusD3(fileId, status) {
    try {
      const response = await this.httpClient.put(`/file-metadata-dim3/files/${fileId}/status`, { status });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteFileD3(fileId) {
    try {
      const response = await this.httpClient.delete(`/file-metadata-dim3/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async retryFileProcessingD3(fileId) {
    try {
      const response = await this.httpClient.post(`/file-metadata-dim3/files/${fileId}/retry`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFiltersD3() {
    try {
      const response = await this.httpClient.get('/file-metadata-dim3/filters');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFilterD3(filterId) {
    try {
      const response = await this.httpClient.get(`/file-metadata-dim3/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createFilterD3(filterConfig) {
    try {
      const response = await this.httpClient.post('/file-metadata-dim3/filters', filterConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateFilterD3(filterId, filterConfig) {
    try {
      const response = await this.httpClient.put(`/file-metadata-dim3/filters/${filterId}`, filterConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteFilterD3(filterId) {
    try {
      const response = await this.httpClient.delete(`/file-metadata-dim3/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getStatsD3() {
    try {
      const response = await this.httpClient.get('/file-metadata-dim3/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Filter Management endpoints
  async getFilters() {
    try {
      const response = await this.httpClient.get('/file-metadata/filters');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getFilter(filterId) {
    try {
      const response = await this.httpClient.get(`/file-metadata/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createFilter(filterConfig) {
    try {
      const response = await this.httpClient.post('/file-metadata/filters', filterConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateFilter(filterId, filterConfig) {
    try {
      const response = await this.httpClient.put(`/file-metadata/filters/${filterId}`, filterConfig);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteFilter(filterId) {
    try {
      const response = await this.httpClient.delete(`/file-metadata/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Stats endpoint
  async getStats() {
    try {
      const response = await this.httpClient.get('/file-metadata/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Dashboard Stats endpoint
  async getDashboardStats() {
    try {
      const response = await this.httpClient.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Agent Metrics endpoints
  async getAgentMetricsRun(runId) {
    try {
      const response = await this.httpClient.get(`/agent-metrics/runs/${runId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentMetricsAgents(window = '24h', agentId = null, options = {}) {
    try {
      const { mode = 'aggregate', granularity = 'auto' } = options || {};
      const params = { window, mode };
      if (mode === 'timeseries' && granularity && granularity !== 'auto') {
        params.granularity = granularity;
      }
      if (agentId !== null && agentId !== undefined && agentId !== '') {
        params.agent_id = agentId;
      }
      const response = await this.httpClient.get('/agent-metrics/agents', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentMetricsModels(window = '24h', agentId = null) {
    try {
      const params = { window };
      if (agentId !== null && agentId !== undefined && agentId !== '') {
        params.agent_id = agentId;
      }
      const response = await this.httpClient.get('/agent-metrics/models', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Agent Management endpoints
  async createAgent(agentData) {
    try {
      const response = await this.httpClient.post('/diy-agents/', agentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgents(page = 1, size = 10, name = null) {
    try {
      const params = { page, size };
      if (name && typeof name === 'string' && name.trim()) {
        params.name = name.trim();
      }
      const response = await this.httpClient.get('/diy-agents/', {
        params
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgent(agentId) {
    try {
      const response = await this.httpClient.get(`/diy-agents/${agentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAgent(agentId, agentData) {
    try {
      const response = await this.httpClient.put(`/diy-agents/${agentId}`, agentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAgent(agentId) {
    try {
      const response = await this.httpClient.delete(`/diy-agents/${agentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async shareAgent(agentId, email) {
    try {
      const response = await this.httpClient.post(`/diy-agents/${agentId}/share`, {
        email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentHistory(agentId) {
    try {
      const response = await this.httpClient.get(`/diy-agents/${agentId}/history`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentLearningConfig(agentId) {
    try {
      const response = await this.httpClient.get(`/diy-agents/${agentId}/learning/config`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAgentLearningConfig(agentId, learningConfig) {
    try {
      const response = await this.httpClient.put(`/diy-agents/${agentId}/learning/config`, {
        learning_config: learningConfig,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentLearningRecords(agentId, params = {}) {
    try {
      const response = await this.httpClient.get(`/diy-agents/${agentId}/learning/records`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAgentLearningRecord(agentId, learningId) {
    try {
      const response = await this.httpClient.delete(`/diy-agents/${agentId}/learning/records/${learningId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async restoreAgentVersion(agentId, historyId) {
    try {
      const response = await this.httpClient.post(`/diy-agents/${agentId}/history/${historyId}/restore`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async executeAgent(payload) {
    try {
      const response = await this.httpClient.post('/dynamic-agent/execute', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getChainedVariables(agentIds) {
    try {
      const response = await this.httpClient.post('/diy-agents/get-chained-variables', {
        agent_ids: agentIds
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Agent Teams endpoints
  async createAgentTeam(teamData) {
    try {
      const response = await this.httpClient.post('/agent-teams/', teamData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentTeams(page = 1, size = 10) {
    try {
      const response = await this.httpClient.get('/agent-teams/', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentTeam(teamId) {
    try {
      const response = await this.httpClient.get(`/agent-teams/${teamId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentTeamByAgent(agentId) {
    try {
      const response = await this.httpClient.get(`/agent-teams/by-agent/${agentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAgentTeam(teamId, teamData) {
    try {
      const response = await this.httpClient.put(`/agent-teams/${teamId}`, teamData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAgentTeam(teamId) {
    try {
      const response = await this.httpClient.delete(`/agent-teams/${teamId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async publishAgent(agentId) {
    try {
      const response = await this.httpClient.post(`/diy-agents/${agentId}/publish`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async unpublishAgent(agentId) {
    try {
      const response = await this.httpClient.post(`/diy-agents/${agentId}/unpublish`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async publishAsTemplate(agentId, templateDescription = null) {
    try {
      const response = await this.httpClient.post(`/diy-agents/${agentId}/publish-as-template`, {
        template_description: templateDescription
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTemplate(agentId) {
    try {
      const response = await this.httpClient.delete(`/diy-agents/${agentId}/template`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTemplates(page = 1, size = 10) {
    try {
      const response = await this.httpClient.get('/agent-templates/', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTemplateByAgentId(agentId) {
    try {
      const response = await this.httpClient.get(`/agent-templates/by-agent/${agentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Human-in-Loop Workflow endpoints
  // Note: These return fetch Response objects for SSE streaming (not axios responses)
  async startHILWorkflowWithUpload(agentId, query, files = [], variables = {}, sessionId = null, userLocation = 'India', userTimezone = 'Asia/Kolkata', filters = null, streaming = true, qualification = null) {
    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('query', query);
      formData.append('streaming', String(streaming));
      if (sessionId) formData.append('session_id', sessionId);
      if (userLocation) formData.append('user_location', userLocation);
      if (userTimezone) formData.append('user_timezone', userTimezone);
      if (variables && Object.keys(variables).length > 0) {
        formData.append('variables', JSON.stringify(variables));
      }
      if (files && files.length > 0) {
        files.forEach(file => formData.append('files', file));
      }
      if (filters && Object.keys(filters).length > 0) {
        formData.append('filters', JSON.stringify(filters));
      }
      // Answering/cancelling a qualifying question raised before the workflow ran.
      if (qualification && typeof qualification === 'object') {
        formData.append('qualification', JSON.stringify(qualification));
      }

      const response = await fetch(`${config.API_BASE_URL}/dynamic-agent/workflow-hil/execute-with-upload`, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async startHILWorkflowWithURL(agentId, query, files = [], variables = {}, sessionId = null, userLocation = 'India', userTimezone = 'Asia/Kolkata', streaming = true) {
    try {
      const payload = {
        agent_id: agentId,
        query: query,
        files: files,
        variables: variables,
        session_id: sessionId,
        user_location: userLocation,
        user_timezone: userTimezone,
        streaming: streaming
      };

      const response = await fetch(`${config.API_BASE_URL}/dynamic-agent/workflow-hil/execute-with-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async continueHILWorkflow(runId, approvedGroupIndex, approvedOutput) {
    try {
      const payload = {
        approved_group_index: approvedGroupIndex,
        approved_output: approvedOutput
      };

      const response = await fetch(`${config.API_BASE_URL}/dynamic-agent/workflow-hil/${runId}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getHILWorkflowStatus(runId) {
    try {
      const response = await this.httpClient.get(`/dynamic-agent/workflow-hil/${runId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async regenerateHILWorkflow(runId, waitingGroupIndex, regenerateInput) {
    try {
      const payload = {
        waiting_group_index: waitingGroupIndex,
        regenerate_input: regenerateInput
      };

      const response = await fetch(`${config.API_BASE_URL}/dynamic-agent/workflow-hil/${runId}/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async continueTeamHIL(runId, approvedOutput) {
    try {
      const payload = {};
      if (approvedOutput !== undefined) {
        payload.approved_output = approvedOutput;
      }

      const response = await fetch(`${config.API_BASE_URL}/agent-teams/team-hil/${runId}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getTeamHILStatus(runId) {
    try {
      const response = await this.httpClient.get(`/agent-teams/team-hil/${runId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async regenerateTeamHIL(runId) {
    try {
      const response = await fetch(`${config.API_BASE_URL}/agent-teams/team-hil/${runId}/regenerate`, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  // ── Single-agent background runs (checkpointing) ────────────────────────
  // Long runs that must survive a dropped connection. Returns immediately with
  // a run_id; the answer is collected by polling getAgentRunStatus. This is not
  // stream resume — there is no partial text, only the finished answer.
  async executeAgentBackground(agentId, query, variables = {}, sessionId = null, userLocation = null, userTimezone = null, filters = null, qualification = null) {
    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('query', query);
      if (sessionId) formData.append('session_id', sessionId);
      if (userLocation) formData.append('user_location', userLocation);
      if (userTimezone) formData.append('user_timezone', userTimezone);
      if (variables && Object.keys(variables).length > 0) {
        formData.append('variables', JSON.stringify(variables));
      }
      if (filters && Object.keys(filters).length > 0) {
        formData.append('filters', JSON.stringify(filters));
      }
      if (qualification && typeof qualification === 'object') {
        formData.append('qualification', JSON.stringify(qualification));
      }

      const response = await this.formDataClient.post('/dynamic-agent/execute-background', formData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAgentRunStatus(runId) {
    try {
      const response = await this.httpClient.get(`/dynamic-agent/runs/${runId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Background Workflow endpoints
  async getBackgroundWorkflowStatus(workflowRunId) {
    try {
      const response = await this.httpClient.get(`/dynamic-agent/workflow-bg/${workflowRunId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTeamBackgroundStatus(runId) {
    try {
      const response = await this.httpClient.get(`/agent-teams/team-bg/${runId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Composio MCP endpoints
  async getComposioApps(params = {}) {
    try {
      const response = await this.httpClient.get('/composio/apps', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getComposioCategories() {
    try {
      const response = await this.httpClient.get('/composio/categories');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getConnectedComposioApps() {
    try {
      const response = await this.httpClient.get('/composio/connected');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async connectComposioApp(appId, payload = {}) {
    try {
      const response = await this.httpClient.post(`/composio/connect/${appId}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async disconnectComposioApp(integrationId) {
    try {
      const response = await this.httpClient.delete(`/composio/disconnect/${integrationId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getComposioAppActions(appId) {
    try {
      const response = await this.httpClient.get(`/composio/apps/${appId}/actions`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async handleComposioOAuthCallback(code, state) {
    try {
      const response = await this.httpClient.post('/composio/oauth/callback', {
        code,
        state
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Generic methods for other endpoints
  async get(endpoint, params = {}) {
    try {
      const response = await this.httpClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async post(endpoint, data = {}) {
    try {
      const response = await this.httpClient.post(endpoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async put(endpoint, data = {}) {
    try {
      const response = await this.httpClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const response = await this.httpClient.delete(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Webhook Management endpoints
  async getWebhookSubscriptions() {
    try {
      const response = await this.httpClient.get('/webhooks');
      return response.data.subscriptions || response.data;
    } catch (error) {
      throw error;
    }
  }

  async createWebhookSubscription(data) {
    try {
      const response = await this.httpClient.post('/webhooks', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteWebhookSubscription(id) {
    try {
      const response = await this.httpClient.delete(`/webhooks/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWebhookLogs(subscriptionId) {
    try {
      const response = await this.httpClient.get(`/webhooks/${subscriptionId}/logs`);
      return response.data.logs || response.data;
    } catch (error) {
      throw error;
    }
  }

  async testWebhookTrigger(subscriptionId, eventType) {
    try {
      const response = await this.httpClient.post(`/webhooks/${subscriptionId}/test`, null, {
        params: { event_type: eventType }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Schedule Management endpoints
  async listSchedules() {
    try {
      const response = await this.httpClient.get('/schedules');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createSchedule(data) {
    try {
      const response = await this.httpClient.post('/schedules', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSchedule(scheduleId) {
    try {
      const response = await this.httpClient.get(`/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateSchedule(scheduleId, data) {
    try {
      const response = await this.httpClient.put(`/schedules/${scheduleId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteSchedule(scheduleId) {
    try {
      const response = await this.httpClient.delete(`/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleSchedule(scheduleId) {
    try {
      const response = await this.httpClient.post(`/schedules/${scheduleId}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async runScheduleNow(scheduleId) {
    try {
      const response = await this.httpClient.post(`/schedules/${scheduleId}/run-now`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async listScheduleLogs(scheduleId, limit = 25) {
    try {
      const response = await this.httpClient.get(`/schedules/${scheduleId}/logs`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async clearScheduleLogs(scheduleId) {
    try {
      const response = await this.httpClient.delete(`/schedules/${scheduleId}/logs`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Agent inbound triggers (webhook / manual / email)
  async listTriggers() {
    try {
      const response = await this.httpClient.get('/triggers');
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data?.triggers || data?.items || data?.data?.triggers || data?.results || [];
    } catch (error) {
      throw error;
    }
  }

  async createTrigger(data) {
    try {
      const response = await this.httpClient.post('/triggers', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTrigger(triggerId) {
    try {
      const response = await this.httpClient.get(`/triggers/${triggerId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTrigger(triggerId, data) {
    try {
      const response = await this.httpClient.put(`/triggers/${triggerId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTrigger(triggerId) {
    try {
      const response = await this.httpClient.delete(`/triggers/${triggerId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleTrigger(triggerId) {
    try {
      const response = await this.httpClient.post(`/triggers/${triggerId}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async runTriggerNow(triggerId, body = {}) {
    try {
      const response = await this.httpClient.post(`/triggers/${triggerId}/run-now`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async listTriggerLogs(triggerId, limit = 50) {
    try {
      const response = await this.httpClient.get(`/triggers/${triggerId}/logs`, {
        params: { limit },
      });
      const data = response.data;
      return Array.isArray(data) ? data : data?.logs || data?.items || [];
    } catch (error) {
      throw error;
    }
  }

  async clearTriggerLogs(triggerId) {
    try {
      const response = await this.httpClient.delete(`/triggers/${triggerId}/logs`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async clearWebhookLogs(subscriptionId) {
    try {
      const response = await this.httpClient.delete(`/webhooks/${subscriptionId}/logs`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;

// Export the axios instances for custom requests if needed
export { defaultAxiosInstance, formDataAxiosInstance };
